import { collection, doc, getDoc, getDocs, onSnapshot, runTransaction, serverTimestamp, setDoc, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';

export function authorName(user) {
  return user?.displayName || user?.email?.split('@')[0] || 'Loc friend';
}

export function userRef(uid) {
  return doc(db, 'users', uid);
}

export function watchUser(uid, callback) {
  return onSnapshot(userRef(uid), (snap) => callback(snap.exists() ? { uid: snap.id, ...snap.data() } : null));
}

/** Mirrors UserService.ensureUserDoc: only writes if the doc doesn't exist yet. */
export async function ensureUserDoc(user) {
  const ref = userRef(user.uid);
  const snap = await getDoc(ref);
  if (snap.exists()) return snap.data();
  const initial = {
    displayName: authorName(user),
    email: user.email || '',
    photoURL: user.photoURL || '',
    bio: '',
    hairType: '',
    method: '',
    locSize: '',
    journalPublic: false,
    followerCount: 0,
    followingCount: 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  await setDoc(ref, initial, { merge: true });
  return initial;
}

export async function updateProfile(uid, { displayName, bio, hairType, method, locSize, startDate }) {
  const payload = {
    displayName: displayName?.trim() ? displayName.trim() : "Loc'd In User",
    bio: (bio || '').trim(),
    updatedAt: serverTimestamp(),
  };
  if (hairType) payload.hairType = hairType;
  if (method) payload.method = method;
  if (locSize) payload.locSize = locSize;
  if (startDate) payload.startDate = startDate;
  await setDoc(userRef(uid), payload, { merge: true });
}

/**
 * Mirrors UserService.setJournalPublic: flips the profile flag AND backfills
 * the denormalized `journalPublic` (and author fields) onto every existing
 * entry, so the public-journeys collection-group query picks them up. Without
 * the backfill, entries keep whatever privacy state existed when they were
 * created and stay invisible to other accounts.
 */
export async function setJournalPublic(uid, value) {
  const snap = await getDoc(userRef(uid));
  const user = snap.data() || {};
  await setDoc(userRef(uid), { journalPublic: value, updatedAt: serverTimestamp() }, { merge: true });

  const entriesSnap = await getDocs(collection(db, 'users', uid, 'entries'));
  let batch = writeBatch(db);
  let writes = 0;
  for (const entryDoc of entriesSnap.docs) {
    batch.set(
      entryDoc.ref,
      {
        authorUid: uid,
        authorName: user.displayName || 'Loc friend',
        authorUsername: user.username || '',
        authorAvatarUrl: user.photoURL || '',
        journalPublic: value,
      },
      { merge: true },
    );
    writes += 1;
    if (writes === 450) {
      await batch.commit();
      batch = writeBatch(db);
      writes = 0;
    }
  }
  if (writes > 0) await batch.commit();
}

export function validateUsername(value) {
  const username = value.trim().toLowerCase();
  if (!/^[a-z0-9_]{3,20}$/.test(username)) {
    return 'Use 3-20 lowercase letters, numbers, or underscores.';
  }
  return null;
}

export function needsUsername(profile) {
  return !profile?.username?.trim();
}

function displayNameFromUsername(username) {
  return username.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

/** Mirrors UserService.claimUsername: same usernames/{username} uniqueness lock. */
export async function claimUsername(uid, username, { setDisplayNameIfEmpty = false } = {}) {
  const normalized = username.trim().toLowerCase();
  const error = validateUsername(normalized);
  if (error) throw new Error(error);
  const usernameRef = doc(db, 'usernames', normalized);
  await runTransaction(db, async (tx) => {
    // Firestore requires ALL reads before ANY writes, so read both docs up front.
    const existing = await tx.get(usernameRef);
    if (existing.exists() && existing.data()?.uid !== uid) {
      throw new Error('That username is taken.');
    }
    const userSnap = setDisplayNameIfEmpty ? await tx.get(userRef(uid)) : null;

    if (!existing.exists()) tx.set(usernameRef, { uid });
    const payload = { username: normalized, updatedAt: serverTimestamp() };
    if (setDisplayNameIfEmpty && !userSnap?.data()?.displayName?.trim()) {
      payload.displayName = displayNameFromUsername(normalized);
    }
    tx.set(userRef(uid), payload, { merge: true });
  });
}

export function dateFromFirestore(value) {
  if (value && typeof value.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'string') {
    const time = Date.parse(value);
    return Number.isNaN(time) ? null : new Date(time);
  }
  return null;
}

export function stageForMonth(monthIndex) {
  if (monthIndex <= 3) return 'Starter';
  if (monthIndex <= 9) return 'Budding';
  if (monthIndex <= 18) return 'Teen';
  return 'Mature';
}

export function monthIndexFor(startDate, date) {
  const raw = (date.getFullYear() - startDate.getFullYear()) * 12 + (date.getMonth() - startDate.getMonth());
  return raw < 0 ? 0 : raw;
}

/** Whole months + day progress within the current calendar month (0–~1). */
export function fractionalMonthIndex(startDate, date = new Date()) {
  const whole = monthIndexFor(startDate, date);
  const daysInMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const dayProgress = Math.min(0.999, Math.max(0, (date.getDate() - 1) / daysInMonth));
  return whole + dayProgress;
}

export function monthStamp(monthIndex) {
  return `MONTH ${String(monthIndex).padStart(2, '0')}`;
}

const METHOD_LABELS = {
  interlocked: 'Interlocked',
  palmRolled: 'Palm rolled',
  freeform: 'Freeform',
  twoStrand: 'Two-strand twists',
  coil: 'Coils',
  braidLocs: 'Braid locs',
};

export function methodLabel(method) {
  return METHOD_LABELS[method] || method;
}

export function relativeTime(value) {
  const date = dateFromFirestore(value);
  if (!date) return 'just now';
  const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
